import SPKToast from '$components/SPKToast/SPKToast';
import { ErrorMessageEn, ErrorMessageEs } from '$constants/alerts';
import { useAppDispatch } from '$hooks/use-app-dispatch.hook';
import { useAppSelector } from '$hooks/use-app-selector.hook';
import {
  companySlice,
  useSavePositionEnhancedMutation,
} from '$store/modules/companies';
import { userLanguage, ValidLanguages } from '$translations/index';
import {
  WorkingDaysValues,
  TIMEZONES,
} from '$types/position/positionPropertyValues';
import { Checkbox } from 'primereact/checkbox';
import { InputText } from 'primereact/inputtext';
import { SelectButton } from 'primereact/selectbutton';
import { Dropdown } from 'primereact/dropdown';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  buildPositionPayload,
  buildPositionFormData,
} from '../buildPositionPayload';

const SPKAdditionalInformation = ({
  onComplete,
  onGoBack,
  isInternal,
}: {
  onComplete: () => void;
  onGoBack: () => void;
  isInternal?: boolean;
}) => {
  const [t] = useTranslation('global');
  const dispatch = useAppDispatch();
  const { employerAddPositionForm } = useAppSelector((state) => state.company);
  const { currencyToUsdConverter } = useAppSelector((state) => state.brand);
  const {
    WorkingHours,
    WorkingDays,
    bonus,
    BonusDetails,
    commission,
    CommissionDetails,
    benefits,
    BenefitsDetails,
    other,
    OtherCompensation,
    file,
    JobDescription,
    DegreeType,
  } = employerAddPositionForm;

  const { handleSubmit, control, watch } = useForm({
    defaultValues: {
      WorkingHours,
      WorkingDays,
      bonus,
      BonusDetails,
      commission,
      CommissionDetails,
      benefits,
      BenefitsDetails,
      other,
      OtherCompensation,
    },
  });

  const {
    bonus: bonusValue,
    commission: commissionValue,
    benefits: benefitsValue,
    other: otherValue,
    WorkingHours: workingHoursValue,
    WorkingDays: workingDaysValue,
    BonusDetails: bonusDescriptionValue,
    CommissionDetails: commissionDescriptionValue,
    BenefitsDetails: benefitsDescriptionValue,
    OtherCompensation: otherDescriptionValue,
  } = watch();

  const [savePositionEnhanced, { isLoading: isSavingPosition }] =
    useSavePositionEnhancedMutation();

  const successSubmitHandler = () => {
    if (isInternal) {
      dispatch(
        companySlice.actions.employerAddPositionOverview({
          ...employerAddPositionForm,
          WorkingHours: workingHoursValue,
          WorkingDays: workingDaysValue,
          bonus: bonusValue,
          BonusDetails: bonusDescriptionValue,
          commission: commissionValue,
          CommissionDetails: commissionDescriptionValue,
          benefits: benefitsValue,
          BenefitsDetails: benefitsDescriptionValue,
          other: otherValue,
          OtherCompensation: otherDescriptionValue,
        })
      );
      onComplete();
      return;
    }

    const formState = {
      ...employerAddPositionForm,
      WorkingHours: workingHoursValue,
      WorkingDays: workingDaysValue,
      bonus: bonusValue,
      BonusDetails: bonusDescriptionValue,
      commission: commissionValue,
      CommissionDetails: commissionDescriptionValue,
      benefits: benefitsValue,
      BenefitsDetails: benefitsDescriptionValue,
      other: otherValue,
      OtherCompensation: otherDescriptionValue,
    };
    const payload = buildPositionPayload(formState, currencyToUsdConverter);
    const formData = buildPositionFormData(payload, file);

    savePositionEnhanced(formData).then((resp) => {
      if ('data' in resp) {
        dispatch(
          companySlice.actions.employerSaveCreatedPositionId(resp.data.Id)
        );
        dispatch(companySlice.actions.resetEmployerAddPositionForm());
        onComplete();
      } else {
        SPKToast(
          userLanguage === ValidLanguages.es
            ? ErrorMessageEs.ServerError
            : ErrorMessageEn.ServerError,
          { type: 'Error' }
        );
      }
    });
  };

  const onErrorSubmitHandler = () => {
    SPKToast(t('EmployerAddPosition.Errors.MissingRequiredFields'), {
      type: 'Error',
    });
  };

  const goBackHandler = () => {
    dispatch(
      companySlice.actions.employerAddPositionOverview({
        ...employerAddPositionForm,
        WorkingHours: workingHoursValue,
        WorkingDays: workingDaysValue,
        bonus: bonusValue,
        BonusDetails: bonusDescriptionValue,
        commission: commissionValue,
        CommissionDetails: commissionDescriptionValue,
        benefits: benefitsValue,
        BenefitsDetails: benefitsDescriptionValue,
        other: otherValue,
        OtherCompensation: otherDescriptionValue,
        JobDescription,
        DegreeType,
      })
    );
    onGoBack();
  };

  return (
    <>
      <h1 className="text-4xl font-extrabold mb-8">
        {t('EmployerAddPosition.AdditionalInformation')}
      </h1>
      <form
        onSubmit={handleSubmit(successSubmitHandler, onErrorSubmitHandler)}
        className="text-spk-mediumContrast text-xs w-full flex flex-col"
      >
        <fieldset className="flex flex-col gap-8" disabled={isSavingPosition}>
          <div className="w-full flex flex-col">
            <label htmlFor="WorkingHours" className="my-1">
              {t('EmployerAddPosition.WorkingHours')}
            </label>
            <Controller
              control={control}
              name="WorkingHours"
              render={({ field }) => (
                <Dropdown
                  {...field}
                  id="WorkingHours"
                  className="my-1 w-full"
                  options={TIMEZONES}
                  placeholder={t(
                    'EmployerAddPosition.Placeholders.SelectAnOption'
                  )}
                  disabled={isSavingPosition}
                />
              )}
            />
          </div>

          <div className="w-full flex flex-col">
            <label htmlFor="WorkingDays" className="my-1">
              {t('EmployerAddPosition.WorkingDays')}
            </label>
            <Controller
              control={control}
              name="WorkingDays"
              render={({ field }) => (
                <SelectButton
                  allowEmpty
                  className="my-1 whitespace-nowrap w-full"
                  options={Object.values(WorkingDaysValues)}
                  {...field}
                  disabled={isSavingPosition}
                />
              )}
            />
          </div>

          <div className="flex flex-col gap-2">
            <div>
              <div className="flex flex-row gap-2 items-center mb-2">
                <Controller
                  control={control}
                  name="bonus"
                  render={({ field }) => (
                    <Checkbox
                      checked={field.value}
                      inputId={field.name}
                      {...field}
                    />
                  )}
                />
                <label htmlFor="bonus" className="text-base">
                  Bonus
                </label>
              </div>
              {bonusValue && (
                <Controller
                  control={control}
                  name="BonusDetails"
                  render={({ field }) => (
                    <InputText
                      placeholder={t(
                        'EmployerAddPosition.Placeholders.RoughIdea'
                      )}
                      {...field}
                      className="w-full mb-4 enabled:focus:!shadow-[0_0_0_0.2rem_#a6d5fa] focus:!border-[#2196f3] hover:!border-[#2196f3]"
                      autoComplete="off"
                    />
                  )}
                />
              )}
            </div>
            <div>
              <div className="flex flex-row gap-2 items-center mb-2">
                <Controller
                  control={control}
                  name="commission"
                  render={({ field }) => (
                    <Checkbox
                      checked={field.value}
                      inputId={field.name}
                      {...field}
                    />
                  )}
                />
                <label htmlFor="commission" className="text-base">
                  {t('EmployerAddPosition.Commission')}
                </label>
              </div>
              {commissionValue && (
                <Controller
                  control={control}
                  name="CommissionDetails"
                  render={({ field }) => (
                    <InputText
                      className="w-full mb-4 enabled:focus:!shadow-[0_0_0_0.2rem_#a6d5fa] focus:!border-[#2196f3] hover:!border-[#2196f3]"
                      placeholder={t(
                        'EmployerAddPosition.Placeholders.RoughIdea'
                      )}
                      {...field}
                      autoComplete="off"
                    />
                  )}
                />
              )}
            </div>
            <div>
              <div className="flex flex-row gap-2 items-center mb-2">
                <Controller
                  control={control}
                  name="benefits"
                  render={({ field }) => (
                    <Checkbox
                      checked={field.value}
                      inputId={field.name}
                      {...field}
                    />
                  )}
                />
                <label htmlFor="benefits" className="text-base">
                  {t('EmployerAddPosition.Benefits')}
                </label>
              </div>
              {benefitsValue && (
                <Controller
                  control={control}
                  name="BenefitsDetails"
                  render={({ field }) => (
                    <InputText
                      className="w-full mb-4 enabled:focus:!shadow-[0_0_0_0.2rem_#a6d5fa] focus:!border-[#2196f3] hover:!border-[#2196f3]"
                      placeholder={t(
                        'EmployerAddPosition.Placeholders.RoughIdea'
                      )}
                      {...field}
                      autoComplete="off"
                    />
                  )}
                />
              )}
            </div>
            <div>
              <div className="flex flex-row gap-2 items-center mb-2">
                <Controller
                  control={control}
                  name="other"
                  render={({ field }) => (
                    <Checkbox
                      checked={field.value}
                      inputId={field.name}
                      {...field}
                    />
                  )}
                />
                <label htmlFor="other" className="text-base">
                  {t('EmployerAddPosition.Other')}
                </label>
              </div>
              {otherValue && (
                <Controller
                  control={control}
                  name="OtherCompensation"
                  render={({ field }) => (
                    <InputText
                      className="w-full mb-4 enabled:focus:!shadow-[0_0_0_0.2rem_#a6d5fa] focus:!border-[#2196f3] hover:!border-[#2196f3]"
                      placeholder={t(
                        'EmployerAddPosition.Placeholders.RoughIdea'
                      )}
                      {...field}
                      autoComplete="off"
                    />
                  )}
                />
              )}
            </div>
          </div>
          <div className="flex flex-row gap-8 w-full justify-center mt-8">
            <button
              type="button"
              onClick={() => goBackHandler()}
              className="text-base text-employer-primary font-semibold"
            >
              {t('EmployerAddPosition.GoBack')}
            </button>
            <button
              type="submit"
              className="rounded-full bg-employer-primary px-4 py-2 !text-spk-softBlue text-base font-semibold"
            >
              {t('EmployerAddPosition.ConfirmAndContinue')}
            </button>
          </div>
        </fieldset>
      </form>
    </>
  );
};

SPKAdditionalInformation.defaultProps = {
  isInternal: false,
};

export default SPKAdditionalInformation;
